import { FunctionComponent, useMemo } from 'react';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import { ILayoutControl, ITestable } from '@belong/types';
import { isString } from '@belong/type-utils';
import { ANALYTICS_STRINGS } from '@belong/analytics';
import { ChipsetWrapper } from './ChoiceChipset.styles';
import ChoiceChip, { IChoice } from '../ChoiceChip';

export const TEST_ID = 'ChoiceChipset';

export enum CHOICE_TYPE {
  radio = 'radio',
  checkbox = 'checkbox'
}

export interface IChoiceChipsetProps extends ITestable, ILayoutControl {
  options: (IChoice | string)[];
  description: string;
  name: string;
  selectedValue?: any | any[];
  isDisabled?: boolean;
  allowMultiSelect?: boolean;
  onChange?: (value: any | any[], selection: number | number[]) => void;
}

/**
 * Collection of ChoiceChips
 */
const ChoiceChipset: FunctionComponent<IChoiceChipsetProps> = props => {
  const {
    options = [],
    selectedValue,
    onChange,
    hasControl,
    isDisabled = false,
    allowMultiSelect = false,
    description
  } = props;
  const inputType = allowMultiSelect ? CHOICE_TYPE.checkbox : CHOICE_TYPE.radio;
  const analytics = useAnalyticsEvents();
  const choices: IChoice[] = useMemo(
    () =>
      options.map(item => {
        if (!isString(item)) {
          return item as IChoice;
        }
        return { label: item as string, value: item as string };
      }),
    [options]
  );

  const getInitialValues = () => {
    if (selectedValue === undefined) {
      return Array.isArray(selectedValue) ? selectedValue : [];
    }
    return Array.isArray(selectedValue) ? selectedValue : [selectedValue];
  };

  // [] for no selection
  const initialValues = getInitialValues();
  const selection = initialValues // returns the index of the selected value from the choices
    .map(value => choices.findIndex(item => item.value === value))
    .filter(idx => idx >= 0);

  const onSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // gets the selected index of the choices
    const idx = choices.findIndex(item => item.value === event.target.value);

    analytics.send({
      category: ANALYTICS_STRINGS.CATEGORY.INTERACTION,
      action: ANALYTICS_STRINGS.ACTION.CHOICE_CHIP_SELECTION,
      component: ANALYTICS_STRINGS.COMPONENT.CHOICE_CHIP,
      label: choices[idx].label
    });

    if (onChange) {
      // onChange for Radio type
      if (inputType === CHOICE_TYPE.radio) {
        onChange(choices[idx].value, idx);
        return;
      }
      // else checkbox
      const newValue = choices[idx].value;
      const newValueAlreadyInValues = initialValues.includes(newValue);

      const nextValues = newValueAlreadyInValues
        ? initialValues.filter(item => item !== newValue)
        : [...initialValues, newValue];
      // get the indexes of the nextValues from the choices and create a new array of indexes
      const nextSelections = choices
        .map((s, i) => {
          return nextValues.includes(s.value) ? i : -1;
        })
        .filter(index => index >= 0);
      onChange(nextValues, nextSelections);
    }
  };

  return (
    <ChipsetWrapper hideLegend legend={description} hasControl={hasControl} onChange={onSelectionChange}>
      {choices.map((item, idx) => (
        <ChoiceChip
          key={`${item.value}${item.label}`}
          isSelected={selection.includes(idx)}
          isDisabled={isDisabled}
          as={inputType}
          name={inputType === CHOICE_TYPE.radio ? props.name : `${props.name}-${idx}`}
          {...item}
          data-testid={`${TEST_ID}-choice-${idx}`}
        />
      ))}
    </ChipsetWrapper>
  );
};

ChoiceChipset.displayName = 'ChoiceChipset';
export default ChoiceChipset;
